<script lang="ts">
    import { get } from "svelte/store";
    import { fly } from "svelte/transition";
    import { onDestroy, onMount } from "svelte";
    import type { Unsubscriber } from "svelte/store";
    import type { WorkAdventureComponent } from "../../../types/component";
    import chevronImg from "../images/chevron.svg";
    import type { MenuItem } from "../../Stores/MenuStore";
    import {
        activeSubMenuStore,
        customMenuIframe,
        menuVisiblilityStore,
        SubMenusInterface,
        subMenusStore,
    } from "../../Stores/MenuStore";
    import { menuInputFocusStore } from "../../Stores/MenuInputFocusStore";
    import { sendMenuClickedEvent } from "../../Api/Iframe/Ui/MenuItem";
    import { LL } from "../../../i18n/i18n-svelte";
    import { triggerClickOnEnterOrSpace } from "../../Utils/svelte-a11y";
    import { analyticsClient } from "../../Administration/AnalyticsClient";
    import ButtonClose from "../Input/ButtonClose.svelte";
    import SettingsSubMenu from "./SettingsSubMenu.svelte";
    import ProfileSubMenu from "./ProfileSubMenu.svelte";
    import AboutRoomSubMenu from "./AboutRoomSubMenu.svelte";
    import ContactSubMenu from "./ContactSubMenu.svelte";
    import CustomSubMenu from "./CustomSubMenu.svelte";
    import ReportSubMenu from "./ReportSubMenu.svelte";
    import ChatSubMenu from "./ChatSubMenu.svelte";
    import ShortcutSubMenu from "./ShortcutSubMenu.svelte";
    import HelpSubMenu from "./HelpSubMenu.svelte";

    // raw, not $state: this holds one of the objects in subMenusStore and is compared back against
    // them with ===, both here and in the markup. A deep-reactive $state would proxy it, and a proxy
    // never matches the object it wraps - so the active item would stop matching itself: no
    // highlight in the list, and includes() below would decide the open menu had been removed from
    // the store and switch away from it, tearing down whatever it was showing. It is only ever
    // reassigned, never mutated, so there is nothing for the proxy to do anyway.
    let activeSubMenu: MenuItem = $state.raw($subMenusStore[$activeSubMenuStore]);
    let activeComponent: WorkAdventureComponent = $state(ProfileSubMenu);
    let props: { url: string; allowApi: boolean; allow?: string } = $state({
        url: "",
        allowApi: false,
        allow: undefined,
    });
    let unsubscriberSubMenuStore: Unsubscriber;
    let unsubscriberActiveSubMenuStore: Unsubscriber;

    onMount(async () => {
        unsubscriberActiveSubMenuStore = activeSubMenuStore.subscribe((value) => {
            if ($subMenusStore.length >= value - 1) {
                switchMenu($subMenusStore[value]).catch((e) =>
                    console.error("Failed to switch menu on activeSubMenuStore change", e),
                );
            }
        });
        unsubscriberSubMenuStore = subMenusStore.subscribe(() => {
            if (!$subMenusStore.some((menu) => menu.key === activeSubMenu?.key)) {
                switchMenu($subMenusStore[$activeSubMenuStore]).catch((e) =>
                    console.error("Failed to switch menu on subMenusStore change", e),
                );
            }
        });

        await switchMenu($subMenusStore[$activeSubMenuStore]);
    });

    onDestroy(() => {
        menuInputFocusStore.set(false);
        if (unsubscriberSubMenuStore) {
            unsubscriberSubMenuStore();
        }
        if (unsubscriberActiveSubMenuStore) {
            unsubscriberActiveSubMenuStore();
        }
    });

    async function switchMenu(menu: MenuItem) {
        if (menu.type === "translated") {
            activeSubMenu = menu;
            activeSubMenuStore.activateByMenuItem(menu);
            switch (menu.key) {
                case SubMenusInterface.profile:
                    activeComponent = ProfileSubMenu;
                    analyticsClient.trackAdminEvent("profile.opened", { source: "menu" });
                    break;
                case SubMenusInterface.settings:
                    activeComponent = SettingsSubMenu;
                    analyticsClient.trackAdminEvent("settings.opened");
                    break;
                case SubMenusInterface.aboutRoom:
                    activeComponent = AboutRoomSubMenu;
                    analyticsClient.trackAdminEvent("menu.credit.opened");
                    break;
                case SubMenusInterface.contact:
                    activeComponent = ContactSubMenu;
                    analyticsClient.trackAdminEvent("menu.contact.opened");
                    break;
                case SubMenusInterface.globalMessages:
                    activeComponent = (await import("./GlobalMessagesSubMenu.svelte")).default;
                    analyticsClient.trackAdminEvent("global_message.opened", { source: "menu" });
                    break;
                case SubMenusInterface.report:
                    activeComponent = ReportSubMenu;
                    analyticsClient.trackAdminEvent("feedback.opened", { feedbackSource: "external_report_url" });
                    break;
                case SubMenusInterface.chat:
                    activeComponent = ChatSubMenu;
                    analyticsClient.trackAdminEvent("menu.chat.opened");
                    break;
                case SubMenusInterface.shortcuts:
                    activeComponent = ShortcutSubMenu;
                    analyticsClient.trackAdminEvent("menu.shortcuts.opened");
                    break;
                case SubMenusInterface.help:
                    activeComponent = HelpSubMenu;
                    break;
            }
        } else {
            // Save custom menu click for analytics
            analyticsClient.trackAdminEvent("menu.custom.opened", { name: menu.key });

            const customMenu = customMenuIframe.get(menu.key);
            if (customMenu !== undefined) {
                activeSubMenu = menu;
                props = { url: customMenu.url, allowApi: customMenu.allowApi, allow: customMenu.allow };
                activeComponent = CustomSubMenu;
            } else {
                sendMenuClickedEvent(menu.key);
                menuVisiblilityStore.set(false);
            }
        }
    }

    function closeMenu() {
        activeSubMenuStore.activateByIndex(0);
        menuVisiblilityStore.set(false);
    }

    function onKeyDown(e: KeyboardEvent) {
        if (e.key === "Escape") {
            closeMenu();
        }
    }

    let subMenuTranslations = $derived(
        $subMenusStore.map((subMenu) => (subMenu.type === "scripting" ? subMenu.label : $LL.menu.sub[subMenu.key]())),
    );

    const SvelteComponent = $derived(activeComponent);
</script>

<svelte:window onkeydown={onKeyDown} />

<!-- TODO HUGO : REMOVE !important -->
<div
    data-testid="settings-menu"
    class="h-3/4 top-0 flex-col gap-3 @md/main-layout:flex-row [@media(min-height:953px)]/main-layout:h-3/4 w-11/12 @xs/main-layout:w-9/12 @lg/main-layout:w-10/12 @2xl:max-w-(--breakpoint-2xl) close-window pointer-events-auto absolute flex right-0 left-0 bottom-0 z-900 m-auto overflow-hidden mr-auto [@media(min-width:1830px)]/main-layout:mr-[auto] @xl/main-layout:mr-40 @sm/main-layout:mr-36"
    transition:fly={{ y: 1000, duration: 150 }}
    onblur={closeMenu}
    aria-label="Меню настроек"
>
    <div class="flex flex-row items-center gap-2">
        <div
            class="menu-nav-sidebar rounded-md2 @md/main-layout:w-[200px] overflow-hidden bg-mlight/80 backdrop-blur w-md relative h-full"
        >
            <!--<h2 class="p-8 text-white/10 h-5 tracking-[1rem] mb-8">{$LL.menu.title()}</h2>-->
            <nav
                class="mt-0 mr-16 @md/main-layout:mr-0 flex flex-row @md/main-layout:flex-col w-full @md/main-layout:w-full items-stretch @md/main-layout:items-start overflow-auto h-full @md/main-layout:overflow-auto p-2.5 @md/main-layout:p-3 gap-1"
            >
                {#each $subMenusStore as submenu, i (`${submenu.key}_${submenu.type}`)}
                    {@const visibleStore = submenu.visible}
                    {#if get(visibleStore)}
                        <div class="flex flex-row items-center justify-center gap-1 w-full group/menu-item relative">
                            <div
                                class=" w-full @md/main-layout:h-full h-1 @md/main-layout:w-1 @md/main-layout:top-0 px-1 @md/main-layout:px-0 @md/main-layout:py-1 flex items-center justify-center absolute -bottom-2 @md/main-layout:-left-2"
                            >
                                <div
                                    class="h-1 @md/main-layout:w-1 bg-mblue rounded-full group-hover/menu-item:h-full transition-all duration-300 z-10 {activeSubMenu?.key ===
                                    submenu.key
                                        ? 'w-full @md/main-layout:h-full'
                                        : 'w-0 @md/main-layout:h-0'} "
                                ></div>
                            </div>

                            <div
                                class="menu-item-container group flex items-center @md/main-layout:justify-start justify-center h-full py-3.5 px-2 relative transition-all w-auto @md/main-layout:w-full @md/main-layout:hover:pl-4 hover:opacity-100 cursor-pointer rounded-md @md/main-layout:rounded-lg overflow-hidden {activeSubMenu?.key ===
                                submenu.key
                                    ? 'active opacity-100 bg-mlight-darker'
                                    : 'opacity-60 hover:bg-white/10'}"
                                role="button"
                                tabindex="0"
                                aria-pressed={activeSubMenu?.key === submenu.key}
                                onclick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    switchMenu(submenu).catch((error) => {
                                        console.error("Failed to switch menu", error);
                                    });
                                }}
                                onkeydown={triggerClickOnEnterOrSpace}
                                transition:fly={{ delay: i * 75, x: 200, duration: 150 }}
                            >
                                <span class="menu-item m-0 relative z-10 bold block @md/main-layout:flex text-nowrap">
                                    {subMenuTranslations[i]}
                                </span>
                                <img
                                    src={chevronImg}
                                    class="hidden @md/main-layout:block absolute transition-all right-4 group-hover:right-6 top-0 bottom-0 m-auto w-4 z-10 {activeSubMenu?.key ===
                                    submenu.key
                                        ? 'opacity-100 group-hover:right-4'
                                        : 'opacity-30'}"
                                    alt=""
                                    draggable="false"
                                />
                            </div>
                        </div>
                    {/if}
                {/each}
            </nav>
        </div>
        <div
            class="p-2 rounded-md2 bg-mlight/80 backdrop-blur-md flex items-center justify-center w-fit @md/main-layout:hidden"
        >
            <ButtonClose onclick={closeMenu} dataTestId="closeMenuBtn" />
        </div>
    </div>
    <div
        class="menu-submenu-container @container/menu-submenu-container w-full bg-mlight/80 rounded-md2 overflow-y relative h-full backdrop-blur overflow-hidden"
    >
        <div
            class="h-full mt-0 rounded-none @md/main-layout:rounded-tl-md2 overflow-y-scroll @md/main-layout:overflow-none"
            id="submenu"
        >
            <SvelteComponent {...props} />
        </div>
    </div>
    <div class="right-menu-side-bar w-fit h-full @md/main-layout:flex flex-col items-start justify-start hidden">
        <div class="rounded-md2 flex items-center justify-center w-fit">
            <ButtonClose onclick={closeMenu} id="closeMenu" dataTestId="closeMenuBtn" />
        </div>
    </div>
</div>

<style>
    .menu-nav-sidebar nav::-webkit-scrollbar {
        display: none;
    }

    #submenu::-webkit-scrollbar {
        width: 4px;
    }
</style>
